import type { ExcalidrawElement } from "@excalidraw/element/types";

import type { ActionName } from "./actions/types";
import type { AppState, ToolType } from "./types";

export const DESKTOP_CHECKOUT_URL =
  "https://opusdesk-hub.lemonsqueezy.com/checkout/buy/39717da7-9528-4947-af04-d675ac2fff1a";

export const DESKTOP_FEATURES = {
  autoShape: ["Automatic shapes", "自動形狀"],
  layout: ["Alignment and distribution", "對齊與平均分布"],
  snapping: ["Object and midpoint snapping", "物件與中點吸附"],
  styles: ["Copy and paste styles", "複製與貼上樣式"],
  crop: ["Image cropping", "圖片裁切"],
  frames: ["Frames", "框架"],
  precision: ["Precision controls and statistics", "精確數值與統計"],
  search: ["Canvas text search", "畫布文字搜尋"],
  links: ["Object links", "物件連結"],
  embeds: ["Web and video embeds", "網頁與影片嵌入"],
  library: ["Local library", "本機素材庫"],
  mermaid: ["Mermaid diagrams", "Mermaid 圖表"],
  highResolution: ["2× / 3× PNG export", "2×／3× PNG 匯出"],
  svg: ["SVG export and copying", "SVG 匯出與複製"],
  editableImages: ["Editable PNG / SVG scenes", "PNG／SVG 可編輯場景"],
  selectionExport: ["Selected-object export", "僅匯出選取物件"],
} as const;
export type DesktopFeature = keyof typeof DESKTOP_FEATURES;

export const desktopCopy = (langCode = "en-US") =>
  langCode.startsWith("zh")
    ? {
        title: "Windows 桌面版功能",
        buy: "購買 Windows EXE",
        body: "此功能包含在 Whiteboard Windows 桌面版。保留完整進階工具，作品儲存在本機。",
        requirements:
          "需要 Windows 64 位元與 Microsoft Edge WebView2 Runtime。",
        keepDrawing: "繼續使用免費網頁版",
      }
    : {
        title: "Available in the Windows desktop app",
        buy: "Buy Windows EXE",
        body: "Get this feature in Whiteboard Desktop for Windows, with complete advanced tools and local storage for your work.",
        requirements:
          "Requires 64-bit Windows and Microsoft Edge WebView2 Runtime.",
        keepDrawing: "Continue with the free web app",
      };
export const desktopFeatureName = (
  feature: DesktopFeature,
  langCode = "en-US",
) => DESKTOP_FEATURES[feature][langCode.startsWith("zh") ? 1 : 0];

const ACTION_FEATURES: Partial<Record<ActionName, DesktopFeature>> = {
  alignTop: "layout",
  alignBottom: "layout",
  alignLeft: "layout",
  alignRight: "layout",
  alignVerticallyCentered: "layout",
  alignHorizontallyCentered: "layout",
  distributeHorizontally: "layout",
  distributeVertically: "layout",
  objectsSnapMode: "snapping",
  midpointSnapping: "snapping",
  copyStyles: "styles",
  pasteStyles: "styles",
  cropEditor: "crop",
  selectAllElementsInFrame: "frames",
  removeAllElementsFromFrame: "frames",
  updateFrameRendering: "frames",
  wrapSelectionInFrame: "frames",
  stats: "precision",
  elementStats: "precision",
  searchMenu: "search",
  hyperlink: "links",
  copyElementLink: "links",
  linkToElement: "links",
  addToLibrary: "library",
  copyAsSvg: "svg",
};
export const desktopFeatureForAction = (
  action: ActionName,
  state: Pick<AppState, "selectedElementIds">,
  value?: unknown,
): DesktopFeature | null => {
  if (action === "changeExportScale" && Number(value) > 1) {
    return "highResolution";
  }
  if (action === "changeExportEmbedScene" && value) {
    return "editableImages";
  }
  if (
    action === "copyAsPng" &&
    Object.values(state.selectedElementIds).some(Boolean)
  ) {
    return "selectionExport";
  }
  return ACTION_FEATURES[action] || null;
};
export const desktopFeatureForTool = (
  tool: ToolType | "custom",
): DesktopFeature | null => {
  if (tool === "autoshape") {
    return "autoShape";
  }
  if (tool === "frame" || tool === "magicframe") {
    return "frames";
  }
  if (tool === "embeddable") {
    return "embeds";
  }
  return null;
};
export const desktopFeatureForElements = (
  elements: readonly ExcalidrawElement[],
): DesktopFeature | null => {
  for (const element of elements) {
    if (element.isDeleted) {
      continue;
    }
    if (element.type === "frame" || element.type === "magicframe") {
      return "frames";
    }
    if (element.type === "embeddable" || element.type === "iframe") {
      return "embeds";
    }
    if (element.link) {
      return "links";
    }
    if (element.type === "image" && element.crop) {
      return "crop";
    }
  }
  return null;
};
export const desktopFeatureForState = (
  state: Partial<AppState>,
): DesktopFeature | null => {
  if (state.openDialog?.name === "ttd") {
    return "mermaid";
  }
  if (state.openDialog?.name === "elementLinkSelector") {
    return "links";
  }
  if (state.openSidebar?.name === "default") {
    return state.openSidebar.tab === "search" ? "search" : "library";
  }
  if (state.stats?.open) {
    return "precision";
  }
  if (state.croppingElementId || state.isCropping) {
    return "crop";
  }
  if (state.editingFrame) {
    return "frames";
  }
  if (state.showHyperlinkPopup === "editor") {
    return "links";
  }
  return state.activeTool ? desktopFeatureForTool(state.activeTool.type) : null;
};

/** Clamp restored/host-supplied preferences without deleting saved artwork. */
export const freeWebState = (state: Partial<AppState>): Partial<AppState> => {
  const next = { ...state };
  if (next.objectsSnapModeEnabled) {
    next.objectsSnapModeEnabled = false;
  }
  if (next.isMidpointSnappingEnabled) {
    next.isMidpointSnappingEnabled = false;
  }
  if (next.exportScale && next.exportScale !== 1) {
    next.exportScale = 1;
  }
  if (next.exportEmbedScene) {
    next.exportEmbedScene = false;
  }
  if (next.stats?.open) {
    next.stats = { ...next.stats, open: false };
  }
  if (next.searchMatches) {
    next.searchMatches = null;
  }
  if (next.openSidebar?.name === "default") {
    next.openSidebar = null;
  }
  if (
    next.openDialog?.name === "ttd" ||
    next.openDialog?.name === "elementLinkSelector"
  ) {
    next.openDialog = null;
  }
  if (next.showHyperlinkPopup) {
    next.showHyperlinkPopup = false;
  }
  if (next.croppingElementId) {
    next.croppingElementId = null;
  }
  if (next.isCropping) {
    next.isCropping = false;
  }
  if (next.editingFrame) {
    next.editingFrame = null;
  }
  if (next.activeEmbeddable) {
    next.activeEmbeddable = null;
  }
  if (next.activeTool && desktopFeatureForTool(next.activeTool.type)) {
    next.activeTool = {
      ...next.activeTool,
      type: "selection",
      customType: null,
      lastActiveTool: null,
    };
  }
  if (next.frameRendering && Object.values(next.frameRendering).some(Boolean)) {
    next.frameRendering = {
      enabled: false,
      clip: false,
      name: false,
      outline: false,
    };
  }
  return next;
};

export class DesktopFeatureUnavailableError extends Error {
  constructor(public feature: DesktopFeature) {
    super("This feature is available in the Windows desktop app.");
    this.name = "DesktopFeatureUnavailableError";
  }
}
